import type { SidebarNavLayout } from '@erp/rpc';
import type {
  RouterNavEntry,
  SidebarNavGroup,
} from '@/routing/nav-from-router';

export type ReconciledLayout = {
  pinnedItems: RouterNavEntry[];
  sections: SidebarNavGroup[];
};

/** A route is pinned via its own nav metadata (`staticData.app.nav.pinned`). */
export function isPinnedEntry(entry: RouterNavEntry): boolean {
  return entry.meta.nav?.pinned === true;
}

export function reconcileNavLayout(
  layout: SidebarNavLayout,
  available: RouterNavEntry[],
): ReconciledLayout {
  const availableMap = new Map(available.map((e) => [e.id, e]));
  // Pinned items render in their own section above the groups; sort them by
  // `nav.order` so the default ordering is deterministic (e.g. Inbox before
  // Agent Monitor) rather than falling back to flatRoutes order.
  const pinnedItems = available
    .filter(isPinnedEntry)
    .sort((a, b) => (a.meta.nav?.order ?? 0) - (b.meta.nav?.order ?? 0));
  const pinnedSet = new Set(pinnedItems.map((e) => e.id));
  const hiddenSet = new Set(layout.hiddenItemIds);
  const hiddenSectionSet = new Set(layout.hiddenSectionIds);
  // Seed with hidden + unused ids so they're never treated as "new" routes and
  // re-added to the sidebar.
  const accountedFor = new Set<string>([
    ...pinnedSet,
    ...layout.hiddenItemIds,
    ...layout.unusedItemIds,
  ]);

  const sections: SidebarNavGroup[] = [];
  for (const section of layout.sections) {
    // A hidden section keeps its items (account for them so they aren't
    // re-added) but renders nothing in the sidebar.
    const sectionHidden = hiddenSectionSet.has(section.id);
    const items: RouterNavEntry[] = [];
    for (const id of section.itemIds) {
      accountedFor.add(id);
      if (sectionHidden || pinnedSet.has(id) || hiddenSet.has(id)) continue;
      const entry = availableMap.get(id);
      if (!entry) continue;
      items.push(entry);
    }
    if (items.length > 0) {
      sections.push({ id: section.id, title: section.title, items });
    }
  }

  const newEntries = available.filter(
    // `defaultHidden` routes never auto-join the sidebar; they surface only in
    // the settings "Unused" bucket until the user opts in. Skipping them here
    // keeps them out of the rendered nav for users with an existing layout.
    (e) => !accountedFor.has(e.id) && !e.meta.nav?.defaultHidden,
  );
  for (const entry of newEntries) {
    const defaultGroup = entry.meta.nav?.group ?? 'Other';
    const existing = sections.find((s) => s.title === defaultGroup);
    if (existing) {
      existing.items.push(entry);
    } else {
      const groupOrder = entry.meta.nav?.groupOrder ?? Infinity;
      let insertIdx = sections.length;
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const sectionOrder =
          section?.items[0]?.meta.nav?.groupOrder ?? Infinity;
        if (groupOrder < sectionOrder) {
          insertIdx = i;
          break;
        }
      }
      // Deterministic id for a synthesized (new-route) group so it stays stable
      // across renders.
      sections.splice(insertIdx, 0, {
        id: `default:${defaultGroup}`,
        title: defaultGroup,
        items: [entry],
      });
    }
  }

  return { pinnedItems, sections };
}
